import { buildTrainingsDeskPath } from "@tennis-matches/core";
import { getUser } from "../auth/index.js";
import {
  listParentBookingTrainerAttentionSessions,
  loadHubRelevantSessions,
  loadTrainers,
  mergePeopleWithMissingIds,
  normalizeSessionPersonIdList,
  resolveViewerOwnTrainerIds,
} from "../trainerstunden/index.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "./hub-scoped-cache.js";

function asStr(v) {
  return String(v ?? "").trim();
}

function personDisplayName(person, fallbackId = "") {
  const full =
    `${asStr(person?.first_name)} ${asStr(person?.last_name)}`.trim();
  if (full) return full;
  const email = asStr(person?.email);
  if (email) return email;
  const fallback = asStr(fallbackId);
  if (
    !fallback ||
    /^[0-9a-f-]{36}$/i.test(fallback) ||
    /^[A-Z0-9_-]{6,}$/.test(fallback)
  ) {
    return "";
  }
  return fallback;
}

function sessionTimeRange(session) {
  const start = asStr(session?.start_time).slice(0, 5);
  const end = asStr(session?.end_time).slice(0, 5);
  if (start && end) return `${start}–${end}`;
  return start || end || "";
}

function sessionTitle(session) {
  return (
    asStr(session?.title) ||
    asStr(session?.group_name) ||
    asStr(session?.label) ||
    ""
  );
}

function bookingPersonId(session) {
  const booking = asStr(
    session?.parent_booking_person_id ?? session?.parentBookingPersonId,
  );
  if (booking) return booking;
  const planned = normalizeSessionPersonIdList(session?.planned_ids);
  return planned[0] || "";
}

function bookingKind(session) {
  const st = asStr(session?.parent_booking_status);
  if (st === "missed_chance") return "missed";
  return "pending";
}

export function buildHubBookingRequestsListHref(sessionId = "") {
  const sp = new URLSearchParams({ filter: "parent-booking" });
  const id = asStr(sessionId);
  if (id) sp.set("session", id);
  return `${buildTrainingsDeskPath("planung")}?${sp.toString()}`;
}

/**
 * @param {object[]} sessions
 * @param {{
 *   people?: object[],
 *   now?: Date,
 *   maxRows?: number,
 *   myTrainerIds?: string[],
 * }} [ctx]
 */
export function buildHubBookingRequestsPeekView(
  sessions,
  { people = [], now = new Date(), maxRows = 3, myTrainerIds = null } = {},
) {
  const attention = listParentBookingTrainerAttentionSessions(sessions, {
    now,
  });
  // Missed zuerst (läuft ab), dann neue pending.
  let rowsSource = [...(attention.missed || []), ...(attention.pending || [])];
  const mine = new Set(
    (Array.isArray(myTrainerIds) ? myTrainerIds : [])
      .map((id) => asStr(id))
      .filter(Boolean),
  );
  if (mine.size) {
    rowsSource = rowsSource.filter((session) =>
      mine.has(asStr(session?.trainer_id)),
    );
  }

  const peopleById = new Map(
    (Array.isArray(people) ? people : [])
      .map((p) => [asStr(p?.id), p])
      .filter(([id]) => id),
  );

  const rows = rowsSource
    .map((session) => {
      const id = asStr(session?.id);
      if (!id) return null;
      const personId = bookingPersonId(session);
      const person = peopleById.get(personId);
      const name = person ? personDisplayName(person) : "";
      const title = sessionTitle(session);
      const date = asStr(session?.date).slice(0, 10);
      const timeRange = sessionTimeRange(session);
      return {
        key: id,
        name: name || title || "",
        title,
        date,
        timeRange,
        kind: bookingKind(session),
        meta: [date, timeRange].filter(Boolean).join(" · "),
        href: buildHubBookingRequestsListHref(id),
      };
    })
    .filter(Boolean);

  const limit = Math.max(1, maxRows);
  return {
    count: rows.length,
    rows: rows.slice(0, limit),
    moreCount: Math.max(0, rows.length - limit),
    href: buildHubBookingRequestsListHref(),
  };
}

const HUB_BOOKING_PEEK_CACHE_MS = 20000;
/** @type {Map<string, Promise<object|null>>} */
const __inflight = new Map();
/** @type {Map<string, { ts: number, value: object|null }>} */
const __cache = new Map();

export function clearHubBookingRequestsPeekCache() {
  __cache.clear();
  __inflight.clear();
}

registerHubCacheClear(clearHubBookingRequestsPeekCache);

/**
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<object|null>}
 */
export async function loadHubBookingRequestsPeek({ force = false } = {}) {
  const scope = await resolveHubReadScope();
  const cacheKey = scope.key;
  const now = Date.now();
  if (!force) {
    const cached = __cache.get(cacheKey);
    if (cached && now - cached.ts < HUB_BOOKING_PEEK_CACHE_MS) {
      return cached.value;
    }
    const inflight = __inflight.get(cacheKey);
    if (inflight) return inflight;
  }

  const started = { ...scope };
  const promise = __loadHubBookingRequestsPeekImpl()
    .then((value) => {
      if (isHubReadScopeCurrent(started)) {
        __cache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__inflight.get(cacheKey) === promise) __inflight.delete(cacheKey);
    });

  __inflight.set(cacheKey, promise);
  return promise;
}

async function __loadHubBookingRequestsPeekImpl() {
  const user = await getUser().catch(() => null);
  const email = asStr(user?.email);
  if (!email && !asStr(user?.id)) return null;

  const [sessionsRes, trainersRes] = await Promise.all([
    loadHubRelevantSessions().catch(() => ({ ok: false, data: [] })),
    loadTrainers().catch(() => ({ ok: false, data: [] })),
  ]);
  const sessions = sessionsRes?.ok ? sessionsRes.data || [] : [];
  const trainers = trainersRes?.ok ? trainersRes.data || [] : [];
  const myTrainerIds = resolveViewerOwnTrainerIds(trainers, {
    authEmail: email,
  });
  if (!myTrainerIds.length) return null;

  const mine = new Set(myTrainerIds.map((id) => asStr(id)).filter(Boolean));
  const attention = listParentBookingTrainerAttentionSessions(sessions);
  const openSessions = [
    ...(attention.pending || []),
    ...(attention.missed || []),
  ].filter((s) => mine.has(asStr(s?.trainer_id)));
  if (!openSessions.length) return null;

  const personIds = [
    ...new Set(openSessions.map((s) => bookingPersonId(s)).filter(Boolean)),
  ];
  let people = [];
  if (personIds.length) {
    const merged = await mergePeopleWithMissingIds([], personIds).catch(
      () => null,
    );
    if (merged?.ok && Array.isArray(merged.data)) people = merged.data;
  }

  const view = buildHubBookingRequestsPeekView(sessions, {
    people,
    myTrainerIds,
  });
  return view.count > 0 ? view : null;
}
